import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, CheckCircle, XCircle, DownloadSimple, ArrowClockwise,
  Wallet, Hourglass, CircleNotch, FunnelSimple,
} from "@phosphor-icons/react";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/Logo";
import AdminUpstoxPanel from "../components/AdminUpstoxPanel";
import AdminTelegramHistory from "../components/AdminTelegramHistory";
import AdminFeedback from "../components/AdminFeedback";

const STATUS_TABS = [
  { k: "requested", label: "Pending", icon: Hourglass },
  { k: "paid", label: "Paid", icon: CheckCircle },
  { k: "rejected", label: "Rejected", icon: XCircle },
  { k: "", label: "All", icon: FunnelSimple },
];

function StatusBadge({ status }) {
  const map = {
    requested: { c: "bg-mod-soft text-mod border-mod-soft", t: "Pending" },
    paid: { c: "bg-pos-soft text-pos border-pos-soft", t: "Paid" },
    rejected: { c: "bg-neg-soft text-neg border-neg-soft", t: "Rejected" },
  };
  const s = map[status] || { c: "bg-surface text-muted-fg border-subtle", t: status };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-sm border font-mono text-[10px] uppercase tracking-widest ${s.c}`}>
      {s.t}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return iso; }
}

function ActionDialog({ row, mode, onClose, onDone }) {
  // mode = 'approve' | 'reject'
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const path = mode === "approve" ? "approve" : "reject";
      const body = mode === "approve" ? { txn_ref: val.trim() } : { reason: val.trim() };
      const { data } = await api.post(`/admin/redemptions/${row.redemption_id}/${path}`, body);
      onDone(data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="action-modal"
    >
      <motion.form
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md border border-subtle rounded-sm bg-app shadow-2xl"
      >
        <div className={`p-4 border-b border-subtle ${mode === "approve" ? "bg-pos-soft" : "bg-neg-soft"}`}>
          <div className={`text-[10px] font-mono uppercase tracking-widest ${mode === "approve" ? "text-pos" : "text-neg"}`}>
            /// {mode === "approve" ? "Approve payout" : "Reject redemption"}
          </div>
          <div className="text-base mt-1 text-primary-fg">
            {row.user_email || row.user_id} · ₹{row.rupees} · {row.payout_method?.toUpperCase()}
          </div>
        </div>

        <div className="p-5 space-y-3">
          <div className="text-xs text-secondary-fg space-y-1 border border-subtle rounded-sm p-3 bg-surface font-mono">
            <div>Credits: {row.credits} → ₹{row.rupees}</div>
            {row.payout_details?.upi_id && <div>UPI: {row.payout_details.upi_id}</div>}
            {row.payout_details?.account_number && <div>A/c: {row.payout_details.account_number} · IFSC: {row.payout_details.ifsc}</div>}
            <div>Requested: {fmtTime(row.requested_at)}</div>
          </div>

          {mode === "approve" ? (
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">UPI / Bank txn ref (optional)</span>
              <input
                data-testid="approve-txn-ref-input"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder="e.g. UPI/2026/0427/X9Z2"
                maxLength={120}
                className="w-full mt-1 px-3 py-2 bg-surface border border-subtle rounded-sm text-primary-fg placeholder:text-muted-fg focus:border-focus font-mono text-sm"
              />
              <div className="text-[10px] text-muted-fg mt-1">Saved in audit trail and CSV exports.</div>
            </label>
          ) : (
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">Rejection reason</span>
              <textarea
                data-testid="reject-reason-input"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder="e.g. Invalid UPI ID — couldn't process payout"
                maxLength={500}
                rows={3}
                className="w-full mt-1 px-3 py-2 bg-surface border border-subtle rounded-sm text-primary-fg placeholder:text-muted-fg focus:border-focus text-sm"
              />
              <div className="text-[10px] text-muted-fg mt-1">User will be refunded {row.credits} credits automatically.</div>
            </label>
          )}

          {err && <div className="text-neg text-xs bg-neg-soft border border-neg-soft px-3 py-2 rounded-sm">{err}</div>}
        </div>

        <div className="p-4 border-t border-subtle flex gap-2 justify-end bg-surface/40">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover text-sm font-mono uppercase tracking-widest"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="action-confirm"
            className={`px-4 py-2 rounded-sm text-sm font-mono uppercase tracking-widest text-white disabled:opacity-50 flex items-center gap-2 ${
              mode === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
            }`}
          >
            {busy && <CircleNotch className="animate-spin" size={14} weight="bold" />}
            Confirm {mode}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [statusFilter, setStatusFilter] = useState("requested");
  const [rows, setRows] = useState([]);
  const [reloading, setReloading] = useState(false);
  const [err, setErr] = useState("");
  const [actionRow, setActionRow] = useState(null); // { row, mode }

  const load = useCallback(async (status) => {
    setReloading(true);
    setErr("");
    try {
      const url = status ? `/admin/redemptions?status=${status}` : "/admin/redemptions";
      const { data } = await api.get(url);
      setRows(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setReloading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") load(statusFilter);
  }, [user, statusFilter, load]);

  const totals = useMemo(() => {
    const t = { requested: 0, paid: 0, rejected: 0, rupees_paid: 0, rupees_pending: 0 };
    rows.forEach((r) => {
      if (r.status in t) t[r.status] += 1;
      if (r.status === "paid") t.rupees_paid += r.rupees || 0;
      if (r.status === "requested") t.rupees_pending += r.rupees || 0;
    });
    return t;
  }, [rows]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <CircleNotch className="animate-spin text-mod" size={20} weight="bold" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center p-6" data-testid="admin-forbidden">
        <div className="text-center max-w-md">
          <XCircle size={32} className="mx-auto text-neg mb-3" weight="bold" />
          <div className="text-primary-fg text-lg font-medium">Admin only</div>
          <div className="text-sm text-secondary-fg mt-1">
            This page is restricted to administrators. Signed in as {user.email}.
          </div>
          <button
            onClick={() => nav("/dashboard")}
            className="mt-5 px-4 py-2 border border-subtle rounded-sm text-sm font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const downloadCsv = () => {
    const url = statusFilter
      ? `${API_BASE}/admin/redemptions/export?status=${statusFilter}`
      : `${API_BASE}/admin/redemptions/export`;
    // Cookies (httpOnly auth) ride along automatically since the iframe shares the origin.
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-app grain" data-testid="admin-page">
      <header className="px-6 py-4 border-b border-subtle bg-surface/40 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="flex items-center gap-2 text-muted-fg hover:text-primary-fg text-sm font-mono uppercase tracking-widest" data-testid="admin-back">
          <ArrowLeft size={14} /> Dashboard
        </Link>
        <span className="text-primary-fg/30">|</span>
        <Logo size={36} />
        <div className="font-mono text-sm tracking-[0.22em] uppercase">
          <span className="text-primary-fg font-semibold">Admin</span>
          <span className="text-muted-fg"> / </span>
          <span className="text-pos">Redemptions</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/admin/blog"
            data-testid="admin-blog-link"
            className="px-3 py-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover hover:text-pos text-[10px] font-mono uppercase tracking-widest"
          >
            Blog CMS
          </Link>
          <Link
            to="/admin/testimonials"
            data-testid="admin-testimonials-link"
            className="px-3 py-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover hover:text-pos text-[10px] font-mono uppercase tracking-widest"
          >
            Testimonials
          </Link>
          <button
            onClick={() => load(statusFilter)}
            disabled={reloading}
            data-testid="reload-btn"
            className="px-3 py-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 disabled:opacity-50"
          >
            <ArrowClockwise size={12} className={reloading ? "animate-spin" : ""} weight="bold" />
            Reload
          </button>
          <button
            onClick={downloadCsv}
            data-testid="download-csv-btn"
            className="px-3 py-1.5 border border-pos-soft bg-pos-soft text-pos rounded-sm text-[10px] font-mono uppercase tracking-widest flex items-center gap-1.5 hover:opacity-90"
          >
            <DownloadSimple size={12} weight="bold" />
            Download CSV {statusFilter && `(${statusFilter})`}
          </button>
        </div>
      </header>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-subtle/30">
        {[
          { label: "Pending", value: totals.requested, accent: "text-mod", testid: "stat-pending" },
          { label: "Paid (page)", value: totals.paid, accent: "text-pos", testid: "stat-paid" },
          { label: "₹ Paid (page)", value: `₹${totals.rupees_paid}`, accent: "text-pos", testid: "stat-rupees-paid" },
          { label: "₹ Pending", value: `₹${totals.rupees_pending}`, accent: "text-mod", testid: "stat-rupees-pending" },
        ].map((s) => (
          <div key={s.label} className="bg-app px-6 py-4" data-testid={s.testid}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">{s.label}</div>
            <div className={`text-2xl tabular-nums font-mono mt-1 ${s.accent}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Upstox connection panel */}
      <AdminUpstoxPanel />

      {/* Telegram broadcast history */}
      <AdminTelegramHistory />

      {/* User feedback inbox */}
      <AdminFeedback />

      {/* Status filter tabs */}
      <div className="px-6 py-3 border-b border-subtle flex items-center gap-2 flex-wrap bg-surface/20">
        {STATUS_TABS.map((t) => {
          const Icon = t.icon;
          const active = statusFilter === t.k;
          return (
            <button
              key={t.k || "all"}
              onClick={() => setStatusFilter(t.k)}
              data-testid={`status-tab-${t.k || "all"}`}
              className={
                "px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1.5 " +
                (active ? "bg-mod-soft border-mod-soft text-mod" : "text-secondary-fg hover:bg-surface-hover")
              }
            >
              <Icon size={12} weight={active ? "fill" : "regular"} />
              {t.label}
            </button>
          );
        })}
        {reloading && <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg ml-2">Loading…</span>}
      </div>

      <div className="p-6">
        {err && <div className="mb-4 p-3 border border-neg-soft bg-neg-soft text-neg text-sm rounded-sm" data-testid="admin-error">{err}</div>}

        {rows.length === 0 && !reloading ? (
          <div className="p-10 border border-subtle rounded-sm bg-surface text-center" data-testid="empty-state">
            <Wallet size={28} className="mx-auto text-muted-fg mb-3" />
            <div className="text-primary-fg font-medium">No redemptions in this view</div>
            <div className="text-sm text-muted-fg mt-1">Switch tabs above or wait for users to redeem credits.</div>
          </div>
        ) : (
          <div className="overflow-x-auto border border-subtle rounded-sm bg-surface">
            <table className="w-full text-sm" data-testid="redemptions-table">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-fg border-b border-subtle bg-app/40">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-right px-4 py-3">Credits → ₹</th>
                  <th className="text-left px-4 py-3">Payout</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Requested</th>
                  <th className="text-left px-4 py-3">Resolved / Ref</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.redemption_id}
                    className="border-b border-subtle/60 hover:bg-app/40"
                    data-testid={`redemption-row-${r.redemption_id}`}
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="text-primary-fg">{r.user_email || r.user_id}</div>
                      {r.user_name && <div className="text-[11px] text-muted-fg">{r.user_name}</div>}
                    </td>
                    <td className="px-4 py-3 text-right align-top font-mono tabular-nums">
                      <div className="text-primary-fg">{r.credits} cr</div>
                      <div className="text-[11px] text-pos">₹{r.rupees}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-primary-fg uppercase font-mono text-[11px]">{r.payout_method}</div>
                      {r.payout_details?.upi_id && <div className="text-[11px] text-muted-fg break-all font-mono">{r.payout_details.upi_id}</div>}
                      {r.payout_details?.account_number && (
                        <div className="text-[11px] text-muted-fg font-mono">
                          {r.payout_details.account_number} · {r.payout_details.ifsc}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StatusBadge status={r.status} />
                      {r.reject_reason && <div className="text-[11px] text-neg mt-1 max-w-[14rem] truncate" title={r.reject_reason}>{r.reject_reason}</div>}
                    </td>
                    <td className="px-4 py-3 align-top text-secondary-fg text-[11px] font-mono">{fmtTime(r.requested_at)}</td>
                    <td className="px-4 py-3 align-top text-secondary-fg text-[11px] font-mono">
                      {r.status === "paid" && (
                        <>
                          <div>{fmtTime(r.approved_at)}</div>
                          {r.txn_ref && <div className="text-pos break-all">{r.txn_ref}</div>}
                        </>
                      )}
                      {r.status === "rejected" && <div>{fmtTime(r.rejected_at)}</div>}
                      {r.status === "requested" && <span className="text-muted-fg">—</span>}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      {r.status === "requested" ? (
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={() => setActionRow({ row: r, mode: "approve" })}
                            data-testid={`approve-${r.redemption_id}`}
                            className="px-2.5 py-1 border border-pos-soft bg-pos-soft text-pos rounded-sm text-[10px] font-mono uppercase tracking-widest hover:opacity-90 flex items-center gap-1"
                          >
                            <CheckCircle size={11} weight="bold" /> Approve
                          </button>
                          <button
                            onClick={() => setActionRow({ row: r, mode: "reject" })}
                            data-testid={`reject-${r.redemption_id}`}
                            className="px-2.5 py-1 border border-neg-soft bg-neg-soft text-neg rounded-sm text-[10px] font-mono uppercase tracking-widest hover:opacity-90 flex items-center gap-1"
                          >
                            <XCircle size={11} weight="bold" /> Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted-fg text-[10px] font-mono uppercase tracking-widest">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {actionRow && (
        <ActionDialog
          row={actionRow.row}
          mode={actionRow.mode}
          onClose={() => setActionRow(null)}
          onDone={() => {
            setActionRow(null);
            load(statusFilter);
          }}
        />
      )}
    </div>
  );
}
